import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Clock, Trash2, Loader2, AlertCircle, Crown, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';
import BrandLogo from '../components/BrandLogo';
import AdminGlobalHistoryPanel from '../components/AdminGlobalHistoryPanel';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { logos, getLogoForVerdict, getVerdictClass } = useBrandSettings();
  const [factChecks, setFactChecks] = useState([]);
  const [sessionChecks, setSessionChecks] = useState([]); // For free users
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userTier, setUserTier] = useState('free'); // admin, premium, free
  const [deletingId, setDeletingId] = useState(null);
  // Admin-only: switch between personal history and anonymized global view
  const [adminView, setAdminView] = useState('personal'); // 'personal' | 'global'

  useEffect(() => {
    const loadHistory = async () => {
      setIsLoading(true);
      try {
        // Get current user
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        }

        // Fetch history - backend determines what to show based on session
        const history = await apiService.getFactCheckHistory(null, 50, 0);
        setUserTier(history.user_tier || 'free');
        
        if (history.user_tier === 'free' || !history.user_tier) {
          // Free users - load from sessionStorage
          const sessionData = sessionStorage.getItem('session_fact_checks');
          if (sessionData) {
            setSessionChecks(JSON.parse(sessionData));
          }
          setFactChecks([]);
        } else {
          // Premium/Admin - show server history
          setFactChecks(history.fact_checks || []);
        }
      } catch (error) {
        console.error('Error loading history:', error);
        // On error, try to load session data for free users
        const sessionData = sessionStorage.getItem('session_fact_checks');
        if (sessionData) {
          setSessionChecks(JSON.parse(sessionData));
        }
        setFactChecks([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, []);

  const handleDeleteFactCheck = async (factCheckId) => {
    setDeletingId(factCheckId);
    try {
      await apiService.deleteFactCheck(factCheckId);
      // Remove from local state
      setFactChecks(prev => prev.filter(fc => fc.fact_check_id !== factCheckId));
    } catch (error) {
      console.error('Error deleting verification:', error);
      alert(error.response?.data?.detail || 'Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Helper to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Get veracity badge color — supports legacy (Fact/Pseudo Fact/False) AND
  // new admin-configured labels (Verified/Inconclusive/Debunked) via getVerdictClass
  const getVeracityColor = (veracity) => {
    const cls = getVerdictClass(veracity);
    if (cls === 'true') return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300';
    if (cls === 'false') return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300';
  };

  const isAdmin = userTier === 'admin';
  const isPremium = userTier === 'premium' || userTier === 'admin';
  const isFree = userTier === 'free';
  
  // Display items based on tier
  const displayItems = isFree ? sessionChecks : factChecks;
  const canDelete = isPremium || isAdmin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            {logos?.default ? (
              <img src={logos.default} alt="KaiCheckIt" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
            ) : (
              <BrandLogo size="responsive" data-testid="brand-logo-header" />
            )}
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {isAdmin && (
              <Badge className="bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 text-xs hidden sm:inline-flex">
                Admin View
              </Badge>
            )}
            {isPremium && !isAdmin && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 text-xs hidden sm:inline-flex">
                Premium
              </Badge>
            )}
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </header>

      {/* History Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">History</h1>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  {isAdmin
                    ? (adminView === 'global' ? 'Anonymized global view (all users)' : 'Your saved verifications')
                    : isPremium ? 'Your saved verifications' : 'Current session only'}
                </p>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 ml-9 sm:ml-0">
              {(isAdmin && adminView === 'global') ? '' : `${displayItems.length} ${displayItems.length === 1 ? 'result' : 'results'}`}
            </div>
          </div>

          {/* Admin-only: view-mode toggle */}
          {isAdmin && (
            <div
              className="mb-6 inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1"
              role="tablist"
              data-testid="admin-view-toggle"
            >
              <button
                type="button"
                role="tab"
                onClick={() => setAdminView('personal')}
                aria-selected={adminView === 'personal'}
                className={`px-3 sm:px-4 py-1.5 text-sm rounded-md transition-colors ${
                  adminView === 'personal'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                data-testid="admin-view-personal-btn"
              >
                My History
              </button>
              <button
                type="button"
                role="tab"
                onClick={() => setAdminView('global')}
                aria-selected={adminView === 'global'}
                className={`px-3 sm:px-4 py-1.5 text-sm rounded-md transition-colors ${
                  adminView === 'global'
                    ? 'bg-purple-600 text-white shadow'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                data-testid="admin-view-global-btn"
              >
                Global (Anonymized)
              </button>
            </div>
          )}

          {/* Admin global view — render panel and skip the personal list flow */}
          {isAdmin && adminView === 'global' && (
            <AdminGlobalHistoryPanel />
          )}

          {/* Personal / default flow (everyone except admin-in-global-mode) */}
          {!(isAdmin && adminView === 'global') && (
          <>
          {/* Free User Upgrade Banner */}
          {isFree && (
            <Alert className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 dark:from-purple-900/20 dark:to-blue-900/20 dark:border-purple-800">
              <Crown className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <AlertDescription className="text-purple-800 dark:text-purple-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <span className="font-semibold">Upgrade to Premium</span> to save your verification history permanently and access it anytime.
                    <span className="block text-sm text-purple-600 dark:text-purple-400 mt-1">
                      Current session results will be lost when you refresh or log out.
                    </span>
                  </div>
                  <Button 
                    onClick={() => navigate('/pricing')}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white whitespace-nowrap"
                    size="sm"
                  >
                    <Crown className="w-4 h-4 mr-1" />
                    Upgrade
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-blue-600 animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-300">Loading...</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 sm:space-y-4">
                {displayItems.map((result) => (
                  <Card key={result.fact_check_id} className="hover:shadow-md transition-shadow overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex gap-3">
                        {/* Verdict Logo */}
                        <div className="flex-shrink-0">
                          {getLogoForVerdict(result.veracity) ? (
                            <img 
                              src={getLogoForVerdict(result.veracity)} 
                              alt={result.veracity} 
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-contain"
                            />
                          ) : (
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${
                              getVerdictClass(result.veracity) === 'true' ? 'bg-green-100 dark:bg-green-900/30' :
                              getVerdictClass(result.veracity) === 'false' ? 'bg-red-100 dark:bg-red-900/30' :
                              'bg-amber-100 dark:bg-amber-900/30'
                            }`}>
                              <Shield className={`w-5 h-5 sm:w-6 sm:h-6 ${
                                getVerdictClass(result.veracity) === 'true' ? 'text-green-600 dark:text-green-400' :
                                getVerdictClass(result.veracity) === 'false' ? 'text-red-600 dark:text-red-400' :
                                'text-amber-600 dark:text-amber-400'
                              }`} />
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0 flex flex-col gap-2">
                          {/* Admin: Show user info */}
                          {isAdmin && (result.user_email || result.user_name) && (
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 pb-2 border-b border-slate-100 dark:border-slate-700">
                              <User className="w-3 h-3" />
                              <span>{result.user_name || 'Unknown'}</span>
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-400">{result.user_email || 'No email'}</span>
                            </div>
                          )}
                          
                          {/* Badges Row */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <Badge className={`${getVeracityColor(result.veracity)} text-xs px-2 py-0.5`}>
                              {result.veracity}
                            </Badge>
                            <Badge variant="outline" className="text-xs px-2 py-0.5 dark:text-slate-300 dark:border-slate-600">
                              {result.confidence}%
                            </Badge>
                            <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto">
                              {formatDate(result.created_at)}
                            </span>
                          </div>
                          
                          {/* Claim Text */}
                          <p className="text-sm sm:text-base text-slate-800 dark:text-slate-100 font-medium line-clamp-2">
                            {result.claim_type === 'image' ? '🖼️ Image Analysis' : result.claim}
                          </p>
                          
                          {/* Summary */}
                          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                            {result.summary}
                          </p>
                          
                          {/* Actions Row */}
                          <div className="flex items-center gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/results`, { state: { result, fromHistory: true } })}
                              className="text-xs flex-1 sm:flex-none dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700"
                            >
                              View Details
                            </Button>
                            
                            {canDelete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50 hover:border-red-300 dark:text-red-400 dark:hover:bg-red-900/20 px-2"
                                  disabled={deletingId === result.fact_check_id}
                                >
                                  {deletingId === result.fact_check_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="max-w-[90vw] sm:max-w-lg dark:bg-slate-800 dark:border-slate-700">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2 text-base sm:text-lg dark:text-slate-100">
                                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                    Delete Verification?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-sm dark:text-slate-400">
                                    This action cannot be undone.
                                    <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 line-clamp-2">
                                        {result.claim_type === 'image' ? 'Image Analysis' : result.claim}
                                      </p>
                                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {result.veracity} • {result.confidence}%
                                      </p>
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteFactCheck(result.fact_check_id)}
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {displayItems.length === 0 && (
                <div className="text-center py-12 sm:py-20 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 mx-2 sm:mx-0">
                  <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-2">
                    {isFree ? 'No checks this session' : 'No history yet'}
                  </p>
                  <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mb-6 px-4">
                    {isFree 
                      ? 'Start verifying content to see results here. Upgrade to save permanently!'
                      : 'Start verifying content to build your history'
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center px-4">
                    <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
                      Start Checking
                    </Button>
                    {isFree && (
                      <Button 
                        onClick={() => navigate('/pricing')} 
                        variant="outline"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20"
                      >
                        <Crown className="w-4 h-4 mr-1" />
                        Upgrade to Premium
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HistoryPage;
